import { modalUtils } from '@/app/core/utils';
import { get as getLatestTnC } from '@/app/features/products/common/services/tnc-service';
import { post as saveUserAcceptance } from '@/app/features/products/common/services/user-acceptance-service';
import { TncLike, NewUserAcceptance } from '@ssar-app/domain';
import { useState, useEffect } from 'react';
import {
  Button,
  Grid,
  GridItem,
  HStack,
  OrderedList,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import { Spin } from '@ssar-app/uikit';

interface TermsAndConditionsProps {
  type: string;
  onAccept: (tncType: string, tncVersion: string) => void;
  onCancel: () => void;
  subCode: string;
}
export const TermsAndConditions = (props: TermsAndConditionsProps) => {
  const [tnC, setTnC] = useState<TncLike>();
  const [loading, setLoading] = useState(true);
  const { onAccept, onCancel, type, subCode } = props;
  useEffect(() => {
    getLatestTnC(type, subCode)
      .then((res) => {
        if (res.resultCode === 0) {
          setTnC(res.data);
        } else {
          modalUtils.showError(res, 'Error while loading Terms and Conditions');
        }
        setLoading(false);
      })
      .catch((err) => {
        onCancel();
        console.error(err);
        modalUtils.showError(err, 'Error while loading Terms and Conditions');
      });
  }, [type, subCode, onCancel]);

  const onAcceptAndContinue = async (): Promise<void> => {
    setLoading(true);
    const userAcceptance: NewUserAcceptance = {
      acceptedDate: new Date(),
      type: tnC?.type || '',
      version: tnC?.version || '',
      subCode: subCode,
    };
    await saveUserAcceptance(userAcceptance)
      .then((response) => {
        if (response.resultCode === 0) {
          onAccept(tnC?.type || '', tnC?.version || '');
        } else {
          modalUtils.showError(response);
        }
      })
      .catch((err) => {
        console.error(err);
        modalUtils.showError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Spin spinning={loading}>
      <VStack align='start'>
        <Text color='gray.500'>
          <small>
            {` (Last Updated: ${dayjs(tnC?.createdDate).format(
              'YYYY-MMM-DD HH:mm:ss  [GMT]ZZ',
            )})`}
          </small>
        </Text>
      </VStack>
      <ReactMarkdown
        components={{
          p: (props) => <Text {...props} mb={2} />,
          ol: (props) => (
            <OrderedList {...props} paddingInlineStart={10} m={0} />
          ),
        }}
      >
        {tnC?.content || ''}
      </ReactMarkdown>
      <Grid>
        <GridItem>
          {props.type === 'ProgramSubmit' ? (
            <Button
              onClick={onCancel}
              colorScheme='blue'
              boxShadow='lg'
              float='right'
              h={8}
              fontSize={'sm'}
            >
              OK
            </Button>
          ) : (
            <HStack justify='end'>
              <Button
                variant={'outline'}
                colorScheme='gray'
                size={'sm'}
                onClick={onCancel}
                sx={{
                  boxShadow: 'lg',
                  _hover: {
                    bg: 'white',
                    color: 'blue.800',
                    borderColor: 'blue.800',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme='blue'
                boxShadow='lg'
                onClick={onAcceptAndContinue}
                h={8}
                fontSize={'sm'}
              >
                Accept
              </Button>
            </HStack>
          )}
        </GridItem>
      </Grid>
    </Spin>
  );
};
